import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  ShieldAlert,
  OctagonAlert,
  EyeOff,
  Ban,
  Zap,
  AlertCircle
} from 'lucide-react';

export interface SecurityFilterNodeData {
  forbiddenTopics?: string;
  antiHallucination?: boolean;
  piiShield?: boolean;
  canViewCost?: boolean;
  canModifyPrices?: boolean;
  canBookRoom?: boolean;
  [key: string]: unknown;
}

export const SecurityFilterNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as SecurityFilterNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // Local draft state to prevent cursor jumping when typing
  const [localForbiddenTopics, setLocalForbiddenTopics] = useState<string>(() => nodeData.forbiddenTopics || '');
  const isTypingRef = useRef<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const syncDebounceTimerRef = useRef<any>(null);

  React.useEffect(() => {
    if (!isTypingRef.current && nodeData.forbiddenTopics !== undefined && nodeData.forbiddenTopics !== localForbiddenTopics) {
      setLocalForbiddenTopics(nodeData.forbiddenTopics);
    }
  }, [nodeData.forbiddenTopics]);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof SecurityFilterNodeData, value: any, allUpdates?: Partial<SecurityFilterNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'SECURITY_FILTER',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[SecurityFilterNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof SecurityFilterNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const handleForbiddenTopicsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextVal = e.target.value;
    const cursor = e.target.selectionStart;
    setLocalForbiddenTopics(nextVal);

    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    syncDebounceTimerRef.current = setTimeout(() => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, forbiddenTopics: nextVal } } : n))
      );
      persistNodeDataDirectly('forbiddenTopics', nextVal);
    }, 450);

    requestAnimationFrame(() => {
      if (textareaRef.current && document.activeElement === textareaRef.current) {
        textareaRef.current.setSelectionRange(cursor, cursor);
      }
    });
  };

  const handleForbiddenTopicsFocus = () => {
    isTypingRef.current = true;
  };

  const handleForbiddenTopicsBlur = () => {
    isTypingRef.current = false;
    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, forbiddenTopics: localForbiddenTopics } } : n))
    );
    persistNodeDataDirectly('forbiddenTopics', localForbiddenTopics);
  };

  const handleToggle = (field: keyof SecurityFilterNodeData) => {
    const newVal = !nodeData[field];
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: newVal } } : n))
    );
    persistNodeDataDirectly(field, newVal);
  };

  const antiHallucination = nodeData.antiHallucination !== false;
  const piiShield = nodeData.piiShield !== false;
  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[380px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#181105]/98 via-[#0F0B03]/98 to-[#080501]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-amber-400 ring-2 ring-amber-400/50 shadow-[0_0_35px_rgba(245,158,11,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-amber-500/40 hover:border-amber-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(245,158,11,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump Toolbar ── */}
      <div className="absolute -top-11 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-auto z-50 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-950/95 backdrop-blur-xl border border-amber-500/40 shadow-[0_0_25px_rgba(245,158,11,0.4)]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPortalData?.('security', id);
          }}
          className="nodrag flex items-center gap-1.5 text-[11px] font-bold text-amber-300 hover:text-amber-200 transition-colors cursor-pointer"
        >
          <ShieldAlert size={13} className="text-amber-400 animate-pulse" />
          <span>Security Studio</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 font-mono border border-amber-500/30">
            HUD ↗
          </span>
        </button>
      </div>

      {/* ── Directional Connection Handles (Left in / Right out to Luna Core) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-security"
        className="!bg-amber-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(245,158,11,1)]"
        title="Input: Chained Security Filter"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-security"
        className="!bg-amber-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(245,158,11,1)]"
        title="Output to Luna Core Hub (Negative Guardrails)"
      />

      {/* Target Connection Handle (Top) */}
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-amber-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(245,158,11,0.8)]"
      />

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-amber-950/80 via-orange-950/50 to-slate-950/80 px-4 py-3 border-b border-amber-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-amber-500/20 border border-amber-400/50 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.4)]">
            <ShieldAlert size={16} className="animate-pulse text-amber-300" />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Security & Guardrails</h4>
            <p className="text-[10px] text-amber-300/80 font-mono">Negative Constraint Filter</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-amber-400 animate-pulse bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-[0_0_8px_rgba(245,158,11,0.3)]">
            GUARDRAIL
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3.5 text-xs">
        
        {/* 1. Forbidden Topics Textarea */}
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-amber-300/90 mb-1.5 flex items-center gap-1.5">
            <Ban size={12} className="text-amber-400" /> Forbidden Topics & Competitors
          </label>
          <textarea
            ref={textareaRef}
            rows={3}
            className="nodrag nowheel w-full bg-slate-950/90 border border-amber-500/20 focus:border-amber-400 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400/30 transition-all placeholder:text-slate-600 resize-none font-sans leading-relaxed"
            placeholder="e.g. Competitor names, Politics, Religion, Unauthorized price cuts, Internal profit margins..."
            value={localForbiddenTopics}
            onChange={handleForbiddenTopicsChange}
            onFocus={handleForbiddenTopicsFocus}
            onBlur={handleForbiddenTopicsBlur}
          />
          <p className="text-[9px] text-slate-400 mt-1">
            Luna will firmly & politely refuse to discuss or validate these subjects.
          </p>
        </div>

        {/* 2. Negative Constraint Toggles */}
        <div className="space-y-2 pt-1 border-t border-white/5">
          
          {/* Toggle A: Anti-Hallucination & No Unapproved Promises */}
          <div
            onClick={() => handleChange('antiHallucination', !antiHallucination)}
            className="nodrag flex items-center justify-between p-2.5 rounded-xl bg-slate-900/90 border border-amber-500/20 transition-all hover:border-amber-500/40 cursor-pointer"
          >
            <div className="flex items-start gap-2 pr-2">
              <OctagonAlert size={14} className="text-amber-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-[11px] font-bold text-slate-100 block">Strict Anti-Hallucination</span>
                <span className="text-[9px] text-slate-400 block leading-tight">
                  Never promise discounts, price cuts, or guarantees outside verified catalog.
                </span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={antiHallucination}
              onChange={() => {}}
              className="nodrag rounded border-amber-500/40 bg-slate-950 text-amber-500 focus:ring-0 pointer-events-none shrink-0"
            />
          </div>

          {/* Toggle B: Anti-Leak PII & Wholesale Price Shield */}
          <div
            onClick={() => handleChange('piiShield', !piiShield)}
            className="nodrag flex items-center justify-between p-2.5 rounded-xl bg-slate-900/90 border border-amber-500/20 transition-all hover:border-amber-500/40 cursor-pointer"
          >
            <div className="flex items-start gap-2 pr-2">
              <EyeOff size={14} className="text-rose-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-[11px] font-bold text-slate-100 block">Zero-Trust PII & Cost Shield</span>
                <span className="text-[9px] text-slate-400 block leading-tight">
                  Redact wholesale costs, database IDs, API tokens, and secret system variables.
                </span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={piiShield}
              onChange={() => {}}
              className="nodrag rounded border-amber-500/40 bg-slate-950 text-amber-500 focus:ring-0 pointer-events-none shrink-0"
            />
          </div>

        </div>

      </div>

      {/* Source Connection Handle (Bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-amber-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

SecurityFilterNode.displayName = 'SecurityFilterNode';
export default SecurityFilterNode;
